import { useState, useMemo } from "react";
import { MessageSquareWarning, ChevronDown, ChevronUp, Download } from "lucide-react";
import { format } from "date-fns";
import { useFrictionLog } from "@/data/FrictionLogContext";
import {
  FRICTION_TYPES, FRICTION_SEVERITIES, FRICTION_STATUSES, MODULE_NAMES,
} from "@/data/frictionLogTypes";
import type { FrictionType, FrictionSeverity, FrictionStatus, FrictionEntry } from "@/data/frictionLogTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const SEVERITY_COLORS: Record<FrictionSeverity, string> = {
  High: "bg-destructive/10 text-destructive border-destructive/30",
  Medium: "bg-warning/10 text-warning border-warning/30",
  Low: "bg-muted text-muted-foreground border-border",
};

const TYPE_COLORS: Record<FrictionType, string> = {
  UX: "bg-primary/10 text-primary border-primary/30",
  Flow: "bg-info/10 text-info border-info/30",
  Speed: "bg-warning/10 text-warning border-warning/30",
  Bug: "bg-destructive/10 text-destructive border-destructive/30",
  "Edge Case": "bg-primary/10 text-primary border-primary/30",
};

const STATUS_COLORS: Record<FrictionStatus, string> = {
  New: "bg-info/10 text-info border-info/30",
  Reviewed: "bg-warning/10 text-warning border-warning/30",
  "In Progress": "bg-primary/10 text-primary border-primary/30",
  Resolved: "bg-success/10 text-success border-success/30",
};

export default function FrictionLog() {
  const { entries, updateEntry } = useFrictionLog();
  const [filterType, setFilterType] = useState<string>("all");
  const [filterModule, setFilterModule] = useState<string>("all");
  const [filterSeverity, setFilterSeverity] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"recency" | "severity">("recency");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    let result = [...entries];
    if (filterType !== "all") result = result.filter((e) => e.frictionType === filterType);
    if (filterModule !== "all") result = result.filter((e) => e.moduleName === filterModule);
    if (filterSeverity !== "all") result = result.filter((e) => e.severity === filterSeverity);
    if (filterStatus !== "all") result = result.filter((e) => e.status === filterStatus);

    if (sortBy === "severity") {
      const order: Record<string, number> = { High: 0, Medium: 1, Low: 2 };
      result.sort((a, b) => (order[a.severity] ?? 2) - (order[b.severity] ?? 2));
    }
    return result;
  }, [entries, filterType, filterModule, filterSeverity, filterStatus, sortBy]);

  const TODAY = new Date();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <MessageSquareWarning className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-foreground">Friction Log</h1>
            <p className="text-[11px] text-muted-foreground">{format(TODAY, "EEEE, MMMM d, yyyy")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">{entries.length} entries</Badge>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={entries.length === 0}
            onClick={() => {
              const payload = {
                exportedAt: new Date().toISOString(),
                count: entries.length,
                entries,
              };
              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `friction-log-${format(TODAY, "yyyy-MM-dd-HHmm")}.json`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast.success(`Exported ${entries.length} entries`);
            }}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export JSON
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-3">
            <FilterSelect label="Type" value={filterType} onChange={setFilterType}
              options={[{ value: "all", label: "All Types" }, ...FRICTION_TYPES.map(t => ({ value: t, label: t }))]} />
            <FilterSelect label="Module" value={filterModule} onChange={setFilterModule} width="w-40"
              options={[{ value: "all", label: "All Modules" }, ...MODULE_NAMES.map(m => ({ value: m, label: m }))]} />
            <FilterSelect label="Severity" value={filterSeverity} onChange={setFilterSeverity}
              options={[{ value: "all", label: "All" }, ...FRICTION_SEVERITIES.map(s => ({ value: s, label: s }))]} />
            <FilterSelect label="Status" value={filterStatus} onChange={setFilterStatus}
              options={[{ value: "all", label: "All" }, ...FRICTION_STATUSES.map(s => ({ value: s, label: s }))]} />
            <FilterSelect label="Sort" value={sortBy} onChange={(v) => setSortBy(v as "recency" | "severity")}
              options={[{ value: "recency", label: "Most Recent" }, { value: "severity", label: "Severity" }]} />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MessageSquareWarning className="h-8 w-8 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No friction entries yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Use the floating "Friction" button on any screen to log issues.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px] w-8"></TableHead>
                  <TableHead className="text-[11px]">Date</TableHead>
                  <TableHead className="text-[11px]">User</TableHead>
                  <TableHead className="text-[11px]">Role</TableHead>
                  <TableHead className="text-[11px]">Module</TableHead>
                  <TableHead className="text-[11px]">Type</TableHead>
                  <TableHead className="text-[11px]">Severity</TableHead>
                  <TableHead className="text-[11px]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((e) => (
                  <EntryRow
                    key={e.id}
                    entry={e}
                    expanded={expandedId === e.id}
                    onToggle={() => setExpandedId(expandedId === e.id ? null : e.id)}
                    onUpdate={updateEntry}
                  />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ─── Reusable filter select ─── */
function FilterSelect({ label, value, onChange, options, width = "w-32" }: {
  label: string; value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[]; width?: string;
}) {
  return (
    <div className="space-y-1">
      <span className="text-[10px] font-medium text-muted-foreground uppercase">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={`h-8 ${width} text-xs`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ─── Expandable entry row ─── */
function EntryRow({ entry: e, expanded, onToggle, onUpdate }: {
  entry: FrictionEntry; expanded: boolean; onToggle: () => void;
  onUpdate: (id: string, updates: Partial<Pick<FrictionEntry, "status" | "adminNotes">>) => void;
}) {
  const [notes, setNotes] = useState(e.adminNotes || "");
  const [status, setStatus] = useState<FrictionStatus>(e.status);

  const handleSave = () => {
    onUpdate(e.id, { status, adminNotes: notes.trim() || undefined });
    toast.success("Entry updated");
  };

  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/30" onClick={onToggle}>
        <TableCell className="px-2">
          {expanded
            ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" />
            : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
        </TableCell>
        <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
          {format(new Date(e.createdAt), "MMM d, h:mm a")}
        </TableCell>
        <TableCell className="text-xs">{e.userId}</TableCell>
        <TableCell className="text-[11px] text-muted-foreground">{e.userRole}</TableCell>
        <TableCell className="text-xs font-medium">{e.moduleName}</TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] ${TYPE_COLORS[e.frictionType]}`}>
            {e.frictionType}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] ${SEVERITY_COLORS[e.severity]}`}>
            {e.severity}
          </Badge>
        </TableCell>
        <TableCell>
          <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[e.status]}`}>
            {e.status}
          </Badge>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={8} className="bg-muted/20 p-4">
            <div className="space-y-3 max-w-2xl">
              {/* Description */}
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Description</Label>
                <p className="text-sm mt-0.5">{e.description}</p>
              </div>

              {/* Context row */}
              <div className="flex flex-wrap gap-4 text-[11px]">
                {e.screenName && (
                  <div>
                    <span className="text-muted-foreground">Screen: </span>
                    <span className="font-medium">{e.screenName}</span>
                  </div>
                )}
                {e.jobId && (
                  <div>
                    <span className="text-muted-foreground">Job: </span>
                    <span className="font-medium">{e.jobId}</span>
                  </div>
                )}
              </div>

              {/* Admin controls */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                <div className="space-y-1">
                  <Label className="text-[11px]">Status</Label>
                  <Select value={status} onValueChange={(v) => setStatus(v as FrictionStatus)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FRICTION_STATUSES.map((s) => (
                        <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px]">Admin Notes</Label>
                  <Textarea
                    value={notes}
                    onChange={(ev) => setNotes(ev.target.value)}
                    placeholder="Internal notes..."
                    className="min-h-[60px] text-xs"
                    onClick={(ev) => ev.stopPropagation()}
                  />
                </div>
              </div>
              <Button size="sm" onClick={(ev) => { ev.stopPropagation(); handleSave(); }}>
                Save Changes
              </Button>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
